import { useMemo, useState } from 'preact/hooks';
import { deleteStoryByCode } from '@/api/storiesClient';
import './delete-story.scss';

const DELETION_CODE_LENGTH = 36;

export function DeleteStoryPage() {
  const [deletionCode, setDeletionCode] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('code') ?? '';
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const normalizedCode = useMemo(() => deletionCode.trim().toLowerCase(), [deletionCode]);
  const canSubmit = normalizedCode.length === DELETION_CODE_LENGTH && !isSubmitting;

  const onRequestDelete = (event: Event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setErrorMessage('');
    setShowConfirmModal(true);
  };

  const onConfirmDelete = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await deleteStoryByCode(normalizedCode);
      setShowConfirmModal(false);
      setDeletionCode('');
      setSuccessMessage('Story deleted permanently.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete story right now.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="ds-page">
      <section className="ds-panel">
        <h1 className="ds-title">Delete story</h1>
        <p className="ds-warning">
          This action is permanent. Once deleted, your story cannot be recovered and will be dissociated from your email.
        </p>

        <form className="ds-form" onSubmit={onRequestDelete}>
          <label className="ds-label" htmlFor="deletion-code">
            Deletion code
          </label>
          <input
            id="deletion-code"
            className="ds-input"
            type="text"
            value={deletionCode}
            onInput={(event) => {
              const target = event.target as HTMLInputElement;
              setDeletionCode(target.value);
            }}
            maxLength={DELETION_CODE_LENGTH}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            autoComplete="off"
          />
          <button className="ds-submit" type="submit" disabled={!canSubmit}>
            Delete story
          </button>
        </form>

        {errorMessage && <p className="ds-feedback ds-feedback--error">{errorMessage}</p>}
        {successMessage && <p className="ds-feedback">{successMessage}</p>}
      </section>

      {showConfirmModal && (
        <div className="ds-modal-backdrop" role="presentation">
          <div className="ds-modal" role="dialog" aria-modal="true" aria-labelledby="delete-story-confirm-title">
            <h2 id="delete-story-confirm-title" className="ds-modal__title">
              Are you sure?
            </h2>
            <p className="ds-modal__text">
              Deleting a story is irreversible. Any records of this story will be unretrievable.
            </p>
            <div className="ds-modal__actions">
              <button type="button" className="ds-modal__cancel" onClick={() => setShowConfirmModal(false)} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="button" className="ds-modal__confirm" onClick={onConfirmDelete} disabled={!canSubmit}>
                Yes, delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
